import React from 'react';
import { flushSync } from 'react-dom';
import SimpleEditor from 'react-simple-code-editor';
import { Highlight } from 'prism-react-renderer';
import { styled } from '@linaria/react';

import { syntaxTheme } from '@/helpers/syntax.helpers';
import { moveCursorWithinInput } from '@/utils';

import VisuallyHidden from '@/components/VisuallyHidden';

const FONT_SIZE = 0.875;
const LINE_HEIGHT = FONT_SIZE * 1.5;

function Editor({
  code,
  language,
  maxHeight,
  handleUpdate,
  handleFormat,
}) {
  const textareaRef = React.useRef(null);
  const focusTrapperRef = React.useRef(null);
  const [ignoreTabKey, setIgnoreTabKey] = React.useState(false);
  const [isFocusingYellowBox, setIsFocusingYellowBox] =
    React.useState(false);

  function handleKeyDown(ev) {
    if (ev.metaKey && ev.key === 's') {
      const input = textareaRef.current._input;
      const cursorAt = input.selectionStart;

      ev.preventDefault();
      handleFormat();

      // NOTE: This doesn't actually work super well. It's off by a few positions. It's still better than doing nothing, since that always shoves the cursor to the very end, but it's not perfect.
      // Rather than fix, though, I should migrate to Sandpack, assuming the Static template stuff can be resolved.
      window.requestAnimationFrame(() => {
        moveCursorWithinInput(input, cursorAt);
      });
    }

    if (ev.key === 'Escape') {
      focusTrapperRef.current.focus();
      setIgnoreTabKey(true);
    }

    if (ev.shiftKey && ev.key === 'Tab') {
      const input = textareaRef.current._input;
      const cursorAt = input.selectionStart;

      if (cursorAt === 0) {
        ev.preventDefault();
        focusTrapperRef.current.focus();
      }
    }
  }

  return (
    <Wrapper
      in={String(ignoreTabKey)}
      style={{ '--max-height': maxHeight }}
    >
      <FocusTrapper
        ref={focusTrapperRef}
        onFocus={() => setIsFocusingYellowBox(true)}
        onBlur={() => setIsFocusingYellowBox(false)}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') {
            ev.stopPropagation();
            ev.preventDefault();

            flushSync(() => {
              setIsFocusingYellowBox(false);
            });

            const input = textareaRef.current._input;
            input.focus();
          }
        }}
      >
        <VisuallyHidden>
          Focus the editor. This will trap focus until you press
          Escape.
        </VisuallyHidden>
      </FocusTrapper>
      <label
        // Don't allow the textarea to be focused if we're focusing the yellow box. They must press "Enter" to start editing.
        // NOTE: This solution isn't perfect, since reversing into it with Shift + Tab is possible. But at least, presumably, they've seen the warning by that point.
        inert={isFocusingYellowBox}
      >
        <VisuallyHidden>Code editor:</VisuallyHidden>
        <CodeEditor
          ref={textareaRef}
          value={code}
          onValueChange={handleUpdate}
          onKeyDown={handleKeyDown}
          translate="no"
          highlight={(code) => (
            <Highlight
              theme={syntaxTheme}
              code={code}
              language={language}
            >
              {({
                className,
                style,
                tokens,
                getLineProps,
                getTokenProps,
              }) => (
                <>
                  {tokens.map((line, i) => (
                    <div key={i} {...getLineProps({ line })}>
                      {line.map((token, key) => (
                        <span
                          key={key}
                          {...getTokenProps({ token })}
                        />
                      ))}
                    </div>
                  ))}
                </>
              )}
            </Highlight>
          )}
        />
      </label>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  max-height: var(--max-height);
  overflow: auto;
  flex: 1;
  /* Firefox scrollbars */
  scrollbar-color: var(--color-gray-300) var(--syntax-bg);
  scrollbar-width: thin;
  isolation: isolate;
`;

const FocusTrapper = styled.button`
  position: absolute;
  inset: 0;
  pointer-events: none;
  user-select: none;
  z-index: 2;

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
    border-radius: 4px;
  }
`;

const CodeEditor = styled(SimpleEditor)`
  /* Update text-select color by setting primary */
  --color-primary: hsl(210deg 13% 50% / 0.25);

  background-color: var(--syntax-bg);
  color: var(--color-text);
  font-family: var(--font-family-mono);
  font-feature-settings: normal;
  padding: 16px;
  border-radius: 0px;
  font-size: ${FONT_SIZE}rem;
  line-height: ${LINE_HEIGHT}rem;

  & > textarea,
  & .token-line,
  & .token-line * {
    outline: none !important;
    font-size: ${FONT_SIZE}rem !important;
    line-height: ${LINE_HEIGHT + 0.125}rem !important;
  }
`;

export default Editor;
